import { useEffect, useRef } from 'react';

// Legacy parity: the /products catalog filter is sticky, per-org, and defaults
// eagerly. Legacy kept filters in a localStorage-persisted store, so a chosen
// catalog survived leaving and returning, and a default was (re)applied
// whenever no catalog was set. The default is the catalog titled "TempoVIP"
// only for the TempoVIP organization (legacy shipped its org id for exactly
// this); every other org defaults to its first catalog (A→Z). The remembered
// selection is scoped per organization so switching orgs restores that org's
// own choice (or its default) instead of leaking another org's catalog.

const DEFAULT_CATALOG_TITLE = 'TempoVIP';
// The org whose default catalog is the flagship "TempoVIP" (legacy constant).
const TEMPO_VIP_ORGANIZATION_ID = '308956ff-5779-4b1c-9213-213840597349';
const STORAGE_KEY_PREFIX = 'products:catalog-filter';

const storageKey = (organizationId: string) =>
  `${STORAGE_KEY_PREFIX}:${organizationId}`;

type CatalogOption = { value: string; label: string };

// The TempoVIP org prefers its flagship catalog; everyone else gets the first
// catalog the BE returned (sorted A→Z).
export const pickDefaultCatalog = (
  catalogs: ReadonlyArray<CatalogOption>,
  organizationId: string
): CatalogOption | undefined =>
  organizationId === TEMPO_VIP_ORGANIZATION_ID
    ? (catalogs.find((c) => c.label === DEFAULT_CATALOG_TITLE) ?? catalogs[0])
    : catalogs[0];

const readStored = (organizationId: string): string[] => {
  try {
    const raw = window.localStorage.getItem(storageKey(organizationId));
    if (raw === null) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === 'string')
      : [];
  } catch {
    return [];
  }
};

// The catalog the /products list is (or would be) filtered by for this org:
// the remembered sticky selection first, else the org's default. Exported so
// other surfaces that need "the consultant's current catalog" — notably the
// Create Product dialog — agree with the list instead of guessing `catalogs[0]`
// and creating products the default list view hides.
export const resolveOrgCatalogId = (
  catalogs: ReadonlyArray<CatalogOption>,
  organizationId: string | null | undefined
): string | undefined => {
  if (!organizationId || catalogs.length === 0) return undefined;
  const remembered = readStored(organizationId).find((id) =>
    catalogs.some((c) => c.value === id)
  );
  return remembered ?? pickDefaultCatalog(catalogs, organizationId)?.value;
};

const writeStored = (organizationId: string, ids: string[]): void => {
  try {
    window.localStorage.setItem(
      storageKey(organizationId),
      JSON.stringify(ids)
    );
  } catch {
    // Non-fatal: storage blocked (private mode). The in-session ref still
    // guards against re-applying within this mount.
  }
};

// Sticky, per-org, default-eager catalog filter. `catalogs` comes from the
// faceted filter-counts (org-scoped server-side), `selectedCatalogIds` is the
// current URL `catalogId` filter, `organizationId` is the active org, and
// `onApply` writes the restored/seeded id(s) back to the URL.
export const useDefaultCatalog = ({
  catalogs,
  selectedCatalogIds,
  organizationId,
  onApply,
}: {
  catalogs: ReadonlyArray<CatalogOption>;
  selectedCatalogIds: string[] | undefined;
  organizationId: string | null | undefined;
  onApply: (catalogIds: string[]) => void;
}): void => {
  const restoredRef = useRef(false);
  const lastWrittenRef = useRef<string | null>(null);
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  useEffect(() => {
    // Need the active org before deciding — also the per-org storage key.
    if (!organizationId) return;

    const inOrg = (id: string) => catalogs.some((c) => c.value === id);

    // Skip redundant writes — `selectedCatalogIds` can change identity without
    // changing value as the URL re-parses each render.
    const persist = (ids: string[]) => {
      const key = JSON.stringify(ids);
      if (key === lastWrittenRef.current) return;
      lastWrittenRef.current = key;
      writeStored(organizationId, ids);
    };

    // After the one-time restore decision, keep this org's store in sync with
    // whatever the consultant filters by (dropping ids not in this org).
    if (restoredRef.current) {
      const current = (selectedCatalogIds ?? []).filter(inOrg);
      // Legacy parity: a catalog is *always* selected. "Clear all" wipes
      // catalogId to empty, but the catalog filter must re-seed its default
      // rather than show "all catalogs". (Legacy reset `isSecondAccess`, which
      // re-armed the same default effect.) Re-apply only when the org has
      // catalogs to pick from.
      if (current.length === 0 && catalogs.length > 0) {
        const target = pickDefaultCatalog(catalogs, organizationId);
        if (target?.value) {
          persist([target.value]);
          onApplyRef.current([target.value]);
          return;
        }
      }
      persist(current);
      return;
    }

    // Wait for the org's catalogs to load before the first decision.
    if (catalogs.length === 0) return;
    restoredRef.current = true;

    // Honor a valid catalog already in the URL (deep link / agent panel),
    // dropping any that don't belong to this org — e.g. a stale catalogId
    // carried over from another org across the org-switch page reload.
    const fromUrl = (selectedCatalogIds ?? []).filter(inOrg);
    if (fromUrl.length > 0) {
      persist(fromUrl);
      // Clean the URL if it carried foreign ids alongside the valid one.
      if (fromUrl.length !== (selectedCatalogIds?.length ?? 0)) {
        onApplyRef.current(fromUrl);
      }
      return;
    }

    // Restore this org's remembered, non-empty selection.
    const valid = readStored(organizationId).filter(inOrg);
    if (valid.length > 0) {
      persist(valid);
      onApplyRef.current(valid);
      return;
    }

    // No catalog set for this org — apply its default. Stays applied until the
    // consultant picks a specific catalog.
    const target = pickDefaultCatalog(catalogs, organizationId);
    if (target?.value) {
      persist([target.value]);
      onApplyRef.current([target.value]);
    }
  }, [catalogs, selectedCatalogIds, organizationId]);
};
